'use client'

import { useTranslations } from 'next-intl'
import Image from 'next/image'
import { Autoplay, Navigation, Pagination } from 'swiper/modules'
import { Swiper, SwiperSlide } from 'swiper/react'

import 'swiper/css'
import 'swiper/css/navigation'
import 'swiper/css/pagination'

import { useCountdown } from '../hooks/useCountdown'
import { IGiftDeal } from '../types/schema'

interface HeroBannerProps {
  deals: IGiftDeal[]
  onBuyNow: (product: IGiftDeal) => void
}

interface HeroSlideProps {
  deal: IGiftDeal
  onBuyNow: (product: IGiftDeal) => void
}

function HeroSlide({ deal, onBuyNow }: HeroSlideProps) {
  const t = useTranslations('')

  // Get promotion end date for this deal
  const getOfferEndDate = () => {
    if (deal?.promoEndDate) {
      const promoEndDate = new Date(deal.promoEndDate)
      promoEndDate.setDate(promoEndDate.getDate() - 1)
      promoEndDate.setHours(23, 59, 59, 999)
      const now = new Date()

      if (promoEndDate > now) {
        return promoEndDate
      }
    }
    const fallbackDate = new Date()
    fallbackDate.setDate(fallbackDate.getDate())
    return fallbackDate
  }

  const countdown = useCountdown(getOfferEndDate())

  return (
    <div className='pm-2 relative flex min-h-[50vh] items-center pb-2 pt-14 md:py-12 lg:min-h-[600px] lg:py-24'>
      {/* Background Image */}
      <div className='absolute inset-0 z-0'>
        <Image
          src={deal.imgURI}
          alt={deal?.name || 'Hero Banner'}
          fill
          priority
          className='object-cover'
          sizes='100vw'
        />
        <div className='absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-black/40'></div>
      </div>

      {/* Main Content */}
      <div className='container relative z-10 mx-auto w-full text-white'>
        <div className='grid grid-cols-1 items-center gap-8 lg:grid-cols-2'>
          {/* Left side - Deal content */}
          <div className='mt-2 rounded-xl border border-white/20 bg-white/10 p-4 shadow-2xl backdrop-blur-md md:mt-4 md:p-8'>

            {/* Deal Title */}
            <h1 className='mb-1 line-clamp-2 h-auto text-xl font-bold leading-tight md:mb-4 md:h-[5rem] md:text-4xl lg:h-[6rem] lg:text-5xl'>
              {deal?.name || t('eGift : Home Page.limited_time_offers')}
            </h1>

            {/* Deal Description */}
            <p className='scrollbar-thin scrollbar-thumb-white/20 scrollbar-track-transparent mb-2 h-[3rem] overflow-y-auto pr-2 text-sm leading-relaxed text-gray-100 md:mb-6 md:h-[5.5rem] md:text-lg'>
              {deal?.detail || t('eGift : Home Page.exclusive_spa_packages')}
            </p>

            {/* Pricing */}
            {deal && (deal.salePrice || deal.originalPrice) && (
              <div className='mb-3 rounded-lg bg-black/30 p-2 backdrop-blur-sm md:mb-8 md:p-4'>
                <div className='flex items-baseline gap-3 md:gap-4'>
                  {deal.salePrice && (
                    <div className='text-xs text-gray-300 line-through md:text-sm'>
                      ฿
                      {Number(deal.originalPrice).toLocaleString(undefined, {
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 2
                      })}
                    </div>
                  )}
                  <div className='text-2xl font-bold text-orange-400 md:text-3xl lg:text-4xl'>
                    ฿
                    {Number(deal.salePrice || deal.originalPrice).toLocaleString(undefined, {
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 2
                    })}
                  </div>

                  {/* Discount Badge */}
                  {(deal?.discountPercent || deal?.savings) && (
                    <div className='self-center'>
                      <span className='inline-block rounded-full bg-orange-400 px-2.5 py-1 text-xs font-bold text-white shadow-lg md:px-3 md:py-1.5 md:text-sm'>
                        {t('eGift : Purchase.ONLINEGIFT_saved')}{' '}
                        {deal.discountPercent
                          ? `${deal.discountPercent}%`
                          : `${Number(deal.savings).toLocaleString(undefined, {
                              minimumFractionDigits: 0,
                              maximumFractionDigits: 2
                            })} THB`}
                      </span>
                    </div>
                  )}
                  
                </div>
              </div>
            )}

            {/* Call to Action Buttons */}
            <div className='flex flex-wrap gap-3 md:gap-4'>
              <button
                data-track="add-to-cart"
                data-name={deal?.name || 'Unknown Deal'}
                onClick={() => onBuyNow(deal)}
                className='text-grey inline-block rounded-lg bg-orange-500 px-4 py-2 text-xs font-bold shadow-lg transition-all duration-200 hover:scale-105 hover:shadow-xl md:px-8 md:py-4 md:text-base'>
                {t('eGift : Home Page.GIFTCERTIFICATE_buy_now').toUpperCase()}
              </button>
            </div>
          </div>

          {/* Right side - Countdown */}
          <div className='mt-4 flex justify-center text-center lg:mt-0 lg:justify-end lg:text-right'>
            <div className='w-full max-w-sm transform rounded-xl border border-white/20 bg-white/10 p-4 shadow-2xl backdrop-blur-md transition-all duration-500 hover:scale-105 md:p-8'>
              <div className='mb-1 text-sm font-medium text-gray-200 md:mb-4 md:text-lg'>
                {t('eGift : Home Page.offer_ends_in')}
              </div>
              <div className='mb-1 font-mono text-xl font-bold text-orange-400 md:text-4xl lg:text-5xl'>
                {countdown.formatted}
              </div>
              <div className='text-xs text-gray-300 md:text-sm'>
                {t('eGift : Home Page.dont_miss_out')}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default function HeroBanner({ deals, onBuyNow }: HeroBannerProps) {
  if (!deals || deals.length === 0) return null

  return (
    <section className='relative'>
      <Swiper
        modules={[Autoplay, Pagination, Navigation]}
        spaceBetween={0}
        slidesPerView={1}
        autoplay={{
          delay: 8000,
          disableOnInteraction: false
        }}
        pagination={{
          clickable: true,
          dynamicBullets: true
        }}
        navigation={true}
        loop={deals.length > 1}
        className='hero-swiper'>
        {deals.map((deal, index) => (
          <SwiperSlide key={deal.id || index}>
            <HeroSlide deal={deal} onBuyNow={onBuyNow} />
          </SwiperSlide>
        ))}
      </Swiper>
      <style jsx global>{`
        .hero-swiper .swiper-pagination-bullet {
          background: rgba(255, 255, 255, 0.5);
          opacity: 1;
        }
        .hero-swiper .swiper-pagination-bullet-active {
          background: #fb923c; /* orange-400 */
        }
        .hero-swiper .swiper-button-next,
        .hero-swiper .swiper-button-prev {
          color: white;
          background: rgba(255, 255, 255, 0.1);
          width: 48px;
          height: 48px;
          border-radius: 50%;
          backdrop-filter: blur(4px);
        }
        .hero-swiper .swiper-button-next:after,
        .hero-swiper .swiper-button-prev:after {
          font-size: 20px;
          font-weight: bold;
        }
        .hero-swiper .swiper-button-next:hover,
        .hero-swiper .swiper-button-prev:hover {
          background: rgba(255, 255, 255, 0.2);
        }
        @media (max-width: 768px) {
          .hero-swiper .swiper-button-next,
          .hero-swiper .swiper-button-prev {
            display: none;
          }
        }
      `}</style>
    </section>
  )
}
